/**
 * PublicMenuContent - Prévia do cardápio público (componente interno)
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, Trash2, Clock, Package, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const PublicMenuContent = ({ restaurant }) => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.filter(cat => cat.active));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Cache-busting header to ensure fresh data
      const response = await axios.get(`${API}/products`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setProducts(response.data.filter(prod => prod.available));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory 
    ? products.filter(product => product.category_id === selectedCategory)
    : products;

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Categoria';
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item => 
      item.id === productId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleWhatsAppOrder = () => {
    if (cart.length === 0) return;
    
    let message = `🍽️ *Pedido - ${restaurant?.name}*\n\n`;
    cart.forEach(item => {
      message += `• ${item.quantity}x ${item.name} - R$ ${(item.price * item.quantity).toFixed(2)}\n`;
    });
    message += `\n💰 *Total: R$ ${cartTotal.toFixed(2)}*`;
    
    const whatsappUrl = `https://wa.me/5511999999999?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Pede é Fácil</h1>
          <p className="text-gray-400">{restaurant?.name}</p>
        </div>
        <div className="flex items-center space-x-4">
          {cart.length > 0 && (
            <button
              onClick={() => setIsCartOpen(true)}
              className="relative glass-button-primary"
            >
              <ShoppingCart size={18} className="mr-2" />
              Carrinho ({cartItemsCount})
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setSelectedCategory('')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedCategory === '' 
              ? 'bg-orange-500 text-white' 
              : 'bg-white/10 text-gray-300 hover:bg-white/20'
          }`}
        >
          Todos
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === category.id 
                ? 'bg-orange-500 text-white' 
                : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
            style={{
              backgroundColor: selectedCategory === category.id ? category.color : undefined
            }}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Products Grid - Otimizado */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredProducts.map((product) => (
          <div key={product.id} className="glass-card product-card group" style={{ padding: '16px' }}>
            {product.image_url && (
              <div className="w-full h-40 rounded-lg overflow-hidden mb-3">
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
                  }}
                />
              </div>
            )}
            
            <div className="space-y-2">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-base font-semibold text-white truncate">{product.name}</h3>
                  <span className="text-xs text-gray-400 bg-white/10 px-2 py-0.5 rounded-full whitespace-nowrap ml-1">
                    {getCategoryName(product.category_id)}
                  </span>
                </div>
                <p className="text-gray-400 text-xs mb-2 line-clamp-2">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-green-400">
                    R$ {product.price.toFixed(2)}
                  </span>
                  {product.preparation_time && (
                    <div className="flex items-center text-gray-400 text-xs">
                      <Clock size={12} className="mr-1" />
                      {product.preparation_time}min
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <div className="flex items-center">
                  <Star className="w-3 h-3 text-yellow-400 fill-current" />
                  <span className="text-yellow-400 text-xs ml-1">4.8</span>
                </div>
                <button
                  onClick={() => addToCart(product)}
                  className="bg-green-500/20 text-green-400 hover:bg-green-500/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="glass-card text-center py-12">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {selectedCategory ? 'Nenhum produto nesta categoria' : 'Nenhum produto disponível'}
          </h3>
          <p className="text-gray-400">
            {selectedCategory 
              ? 'Tente selecionar outra categoria' 
              : 'Adicione produtos ao cardápio primeiro'
            }
          </p>
        </div>
      )}

      {/* Cart Modal */}
      <Dialog open={isCartOpen} onOpenChange={setIsCartOpen}>
        <DialogContent className="glass-modal max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center">
              <ShoppingCart size={24} className="mr-2" />
              Seu Pedido
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {cart.length > 0 ? cart.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <div className="flex-1">
                  <h4 className="text-white font-medium">{item.name}</h4>
                  <p className="text-green-400">R$ {item.price.toFixed(2)} cada</p>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                    className="w-8 h-8 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center"
                  >
                    -
                  </button>
                  <span className="text-white w-8 text-center">{item.quantity}</span>
                  <button
                    onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                    className="w-8 h-8 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center"
                  >
                    +
                  </button>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="ml-2 text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-400">
                <ShoppingCart size={48} className="mx-auto mb-4" />
                <p>Seu carrinho está vazio</p>
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <div className="flex justify-between items-center mb-4">
                <span className="text-xl font-bold text-white">Total:</span>
                <span className="text-2xl font-bold text-green-400">
                  R$ {cartTotal.toFixed(2)}
                </span>
              </div>
              <button
                onClick={handleWhatsAppOrder}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-colors"
              >
                Enviar Pedido via WhatsApp
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default PublicMenuContent;
